<template>
  <div data-testid="side-nav" v-if="clientload">
    <v-navigation-drawer
      data-testid="side-nav__drawer"
      :class="drawerClasses"
      permanent
      touchless
      :width="drawerWidth"
    >
      <financial-close-cycle-side-nav
        data-testid="side-nav__fbc-cycle"
        v-if="navContext === 'fbc-cycle'"
        :mini="mini"
        :selected-item="selectedItem"
        :cycle-id="cycleId"
        @update:selectedItem="$emit('update:selectedItem', $event)"
        @toggle-mini="$emit('toggle-mini')"
      />

      <financial-close-settings-side-nav
        data-testid="side-nav__fbc-settings"
        v-else-if="navContext === 'fbc-settings'"
        :mini="mini"
        :selected-item="selectedItem"
        @update:selectedItem="$emit('update:selectedItem', $event)"
        @toggle-mini="$emit('toggle-mini')"
      />

      <app-menu
        data-testid="side-nav__app-menu"
        v-else-if="!isFinancialClose"
        :mini="mini"
        :menu="$store.state.menu"
        :selected-item="selectedItem"
        :open-menu="openMenu"
        :applist="applist"
        :fd-support-widget="fdSupportWidget"
        :chatbot-enabled="chatbotEnabled"
        @update:selectedItem="$emit('update:selectedItem', $event)"
        @update:openMenu="$emit('update:openMenu', $event)"
        @logout="$emit('logout')"
        @edit-profile="$emit('edit-profile')"
        @toggle-mini="$emit('toggle-mini')"
      />
    </v-navigation-drawer>
  </div>
</template>

<script>
import AppMenu from './AppMenu.vue';
import FinancialCloseCycleSideNav from './navigation/FinancialClose/FinancialCloseCycleSideNav.vue';
import FinancialCloseSettingsSideNav from './navigation/FinancialClose/FinancialCloseSettingsSideNav.vue';
import {
  extractCycleIdFromPath,
  extractCycleIdFromRoute
} from './navigation/FinancialClose/financialCloseTopNavUtils';
import constants from '@/plugins/constants';

export default {
  name: 'SideNav',
  components: {
    AppMenu,
    FinancialCloseCycleSideNav,
    FinancialCloseSettingsSideNav
  },
  props: {
    clientload: { type: Boolean, default: false },
    mini: { type: Boolean, default: false },
    selectedItem: { type: String, default: null },
    openMenu: { type: Boolean, default: false },
    applist: { type: Array, default: () => [] },
    fdSupportWidget: { type: Boolean, default: false },
    chatbotEnabled: { type: Boolean, default: false },
    navContext: { type: String, default: 'app' }
  },
  emits: ['update:selectedItem', 'update:openMenu', 'logout', 'edit-profile', 'toggle-mini'],
  computed: {
    isFbcNavShell() {
      return this.navContext === 'fbc-cycle' || this.navContext === 'fbc-settings';
    },
    drawerClasses() {
      const drawer = this.$constants.STYLES.SIDE_NAV_DRAWER;
      const { CLASS: C } = drawer;
      const sizeClass = this.mini ? C.FBC_NAV_DRAWER_MINI : C.FBC_NAV_DRAWER_EXPANDED;
      const fbcShellBase = [C.NAV_DRAWER, C.HEIGHT_SCREEN, C.FBC_NAV_DRAWER, sizeClass];

      if (this.isFbcNavShell) {
        return fbcShellBase;
      }
      if (!this.isFinancialClose) {
        return [C.NAV_DRAWER, C.HEIGHT_SCREEN, C.FBC_NAV_DRAWER, C.APP_MENU_NAV_DRAWER, sizeClass];
      }
      return [
        C.NAV_DRAWER,
        C.HEIGHT_SCREEN,
        this.mini ? C.LEGACY_MINI_NAV : C.LEGACY_EXPANDED_PADDING
      ];
    },
    drawerWidth() {
      const { WIDTH_COLLAPSED, WIDTH_EXPANDED } = this.$constants.STYLES.SIDE_NAV_DRAWER;
      if (this.isFbcNavShell || !this.isFinancialClose) {
        return this.mini ? WIDTH_COLLAPSED : WIDTH_EXPANDED;
      }
      return this.mini
        ? this.$constants.STYLES.SIZE.MINI_NAV
        : this.$constants.STYLES.SIZE.EXPAND_NAV;
    },
    menuContextPath() {
      return constants.menuContextPath(this.$route.path || '');
    },
    isFinancialClose() {
      return this.menuContextPath.includes(constants.PRODUCT_KEYS.FINANCIAL_CLOSE);
    },
    cycleId() {
      if (constants.isManualsPath(this.$route.path)) {
        return extractCycleIdFromPath(this.menuContextPath);
      }
      return extractCycleIdFromRoute(this.$route);
    }
  }
};
</script>

<style scoped>
/* Figma: single flush right border — no Vuetify border + no gap into main */
.fbc-nav-drawer {
  background-color: var(--grey-50, #f7f7f7) !important;
  border-right: 1px solid var(--grey-200, #cbcbcc) !important;
  box-shadow: none !important;
}

:deep(.fbc-nav-drawer.v-navigation-drawer) {
  border-right: 1px solid var(--grey-200, #cbcbcc) !important;
}

:deep(.fbc-nav-drawer .v-navigation-drawer__border) {
  display: none !important;
  width: 0 !important;
}

:deep(.fbc-nav-drawer .v-navigation-drawer__content) {
  overflow-x: hidden;
}

/* Non-FBC AppMenu uses AppMenuSideNav.scss (FBC-aligned rail). */
</style>

<style lang="scss">
@import './AppMenuSideNav.scss';
</style>
